// Typed, validated reader for observability-related env vars.
// Phase 1.2 scaffold — values are not consumed yet; later phases import `logEnv`.
//
// CRA exposes only REACT_APP_* vars at build time via process.env.
// All reads happen once at module load; the resulting object is frozen.

export type LogEnvironment = 'dev' | 'staging' | 'prod';
export type LogLevelName = 'debug' | 'info' | 'warn' | 'error';
export type LogTransport = 'none' | 'localFile' | 'nr' | 'nr+sentry';

export interface LogEnv {
  env: LogEnvironment;
  appVersion: string;
  buildSha: string;
  minLevel: LogLevelName;
  transport: LogTransport;
  batchSize: number;
  flushMs: number;
  nrIngestKey: string;
  nrLogEndpoint: string;
}

function readString(key: string, fallback: string): string {
  const raw = process.env[key];
  return raw && raw.length > 0 ? raw : fallback;
}

function readInt(key: string, fallback: number): number {
  const raw = process.env[key];
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

function readEnum<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const raw = process.env[key];
  return (allowed as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;
}

export const logEnv: Readonly<LogEnv> = Object.freeze({
  env: readEnum<LogEnvironment>('REACT_APP_ENV', ['dev', 'staging', 'prod'] as const, 'dev'),
  appVersion: readString('REACT_APP_APP_VERSION', '0.0.0'),
  buildSha: readString('REACT_APP_BUILD_SHA', 'unknown'),
  minLevel: readEnum<LogLevelName>(
    'REACT_APP_LOG_MIN_LEVEL',
    ['debug', 'info', 'warn', 'error'] as const,
    'info',
  ),
  transport: readEnum<LogTransport>(
    'REACT_APP_LOG_TRANSPORT',
    ['none', 'localFile', 'nr', 'nr+sentry'] as const,
    'none',
  ),
  batchSize: readInt('REACT_APP_LOG_BATCH_SIZE', 20),
  flushMs: readInt('REACT_APP_LOG_FLUSH_MS', 5000),
  nrIngestKey: readString('REACT_APP_NR_BROWSER_INGEST_KEY', ''),
  nrLogEndpoint: readString('REACT_APP_NR_LOG_ENDPOINT', 'https://log-api.newrelic.com/log/v1'),
});

export function isProd(): boolean {
  return logEnv.env === 'prod';
}

export function isDev(): boolean {
  return logEnv.env === 'dev';
}
